import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, HeartCrack } from "lucide-react";
import { api, guestHeaders, getGuestSession, setGuestSession, clearGuestSession, formatError } from "../lib/api";
import { GuestCodeForm } from "./GuestCodeForm";
import { Reveal } from "./Reveal";
import { TalaveraStrip } from "./TalaveraStrip";

const RsvpForm = ({ session, onReset }) => {
  const [attending, setAttending] = useState(session.rsvp?.attending ?? null);
  const [message, setMessage] = useState(session.rsvp?.message ?? "");
  const [done, setDone] = useState(Boolean(session.rsvp));
  const [loading, setLoading] = useState(false);

  const reset = () => {
    clearGuestSession();
    onReset();
  };

  const submit = async () => {
    if (attending === null) {
      toast.error("Selecciona si podrás acompañarnos");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/rsvp", { attending, message }, { headers: guestHeaders() });
      setGuestSession({ ...session, rsvp: data });
      setDone(true);
      toast.success("¡Gracias por confirmar!");
    } catch (e) {
      if (e?.response?.status === 401) {
        reset();
      } else {
        toast.error(formatError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Reveal data-testid="rsvp-success" className="mt-12 border border-clay/20 rounded-3xl bg-cream p-10 text-center">
        <p className="font-script text-4xl text-clay">¡Gracias, {session.name}!</p>
        <p className="mt-4 text-stone text-sm leading-relaxed">
          {attending
            ? "Tu asistencia está confirmada. No vemos la hora de celebrar contigo."
            : "Sentimos que no puedas acompañarnos. Te llevaremos presente."}
        </p>
        {message && <p className="mt-6 font-serif italic text-lg text-ink">“{message}”</p>}
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
          <button
            data-testid="rsvp-edit-button"
            onClick={() => setDone(false)}
            className="rounded-full border border-clay text-clay px-8 py-3 text-[11px] uppercase tracking-[0.25em] hover:bg-clay hover:text-cream transition-colors"
          >
            Editar respuesta
          </button>
          <button
            data-testid="rsvp-switch-code"
            onClick={reset}
            className="rounded-full text-stone px-8 py-3 text-[11px] uppercase tracking-[0.25em] hover:text-clay transition-colors"
          >
            Usar otro código
          </button>
        </div>
      </Reveal>
    );
  }

  const optionBase = "rounded-3xl border p-8 flex flex-col items-center gap-3 transition-colors duration-300";
  return (
    <Reveal className="mt-12 border border-clay/20 rounded-3xl bg-cream p-8 md:p-10" data-testid="rsvp-form-panel">
      <p className="font-script text-4xl text-clay text-center">Hola, {session.name}</p>
      <p className="text-center text-stone text-sm mt-3">¿Nos acompañas el 10 de octubre?</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
        <button
          data-testid="rsvp-attending-yes"
          onClick={() => setAttending(true)}
          className={`${optionBase} ${attending === true ? "bg-clay text-cream border-clay" : "border-clay/25 text-ink hover:border-clay"}`}
        >
          <Heart size={26} strokeWidth={1.5} />
          <span className="font-serif text-xl">Sí, ahí estaré</span>
        </button>
        <button
          data-testid="rsvp-attending-no"
          onClick={() => setAttending(false)}
          className={`${optionBase} ${attending === false ? "bg-ink text-cream border-ink" : "border-clay/25 text-ink hover:border-clay"}`}
        >
          <HeartCrack size={26} strokeWidth={1.5} />
          <span className="font-serif text-xl">No podré acompañarlos</span>
        </button>
      </div>
      <textarea
        data-testid="rsvp-message-input"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        placeholder="Escribe un mensaje para los novios (opcional)"
        className="mt-6 w-full bg-cream border border-clay/25 rounded-2xl px-6 py-4 text-sm text-ink placeholder:text-stone/50 focus:outline-none focus:border-clay transition-colors resize-none"
      />
      <button
        data-testid="rsvp-submit-button"
        onClick={submit}
        disabled={loading}
        className="mt-6 w-full rounded-full bg-clay text-cream py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-clay-deep transition-colors disabled:opacity-60"
      >
        {loading ? "Enviando…" : "Enviar confirmación"}
      </button>
      <button
        data-testid="rsvp-switch-code"
        onClick={reset}
        className="mt-4 w-full text-center text-stone text-[11px] uppercase tracking-[0.25em] hover:text-clay transition-colors"
      >
        ¿No eres {session.name}? Usa otro código
      </button>
    </Reveal>
  );
};

export const Rsvp = () => {
  const [session, setSession] = useState(getGuestSession());

  useEffect(() => {
    const sync = () => setSession(getGuestSession());
    window.addEventListener("guest-session", sync);
    return () => window.removeEventListener("guest-session", sync);
  }, []);

  return (
    <section id="rsvp" data-testid="rsvp-section" className="relative py-28 md:py-40 px-6 bg-sand overflow-hidden">
      <div className="relative z-10 max-w-xl mx-auto">
        <Reveal>
          <TalaveraStrip count={5} className="mb-10" />
          <p className="font-script text-clay text-3xl md:text-4xl text-center">Acompáñanos</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-center mt-3 text-ink">
            Confirma tu asistencia
          </h2>
          {!session && (
            <p className="mt-6 text-center text-stone text-sm leading-relaxed">
              Ingresa el código que viene en tu invitación para confirmar.
            </p>
          )}
        </Reveal>
        {session ? (
          <RsvpForm session={session} onReset={() => setSession(null)} />
        ) : (
          <Reveal delay={0.15}>
            <GuestCodeForm onSuccess={setSession} />
          </Reveal>
        )}
        <Reveal delay={0.2}>
          <TalaveraStrip count={5} className="mt-12" />
        </Reveal>
      </div>
    </section>
  );
};
